import { v4 as uuidv4 } from 'uuid';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { WidgetComponent } from '@zendesk/forethought-elements';
import {
  AssistPreviewSliceState,
  initialAssistPreviewState,
  PreviewLog,
  TicketEvent,
} from './types';
import type {
  AssistPreviewResponse,
  PreviewIntentFilter,
  RelatedArticle,
  StreamChunk,
} from 'src/services/assist-preview/types';
import type { RootState } from 'src/store/rootReducer';

const assistPreviewSlice = createSlice({
  initialState: initialAssistPreviewState,
  name: 'assistPreview',
  reducers: {
    addPreviewLog(
      state: AssistPreviewSliceState,
      action: PayloadAction<PreviewLog>,
    ) {
      state.previewLogs.push(action.payload);
    },

    addTicketEvent(
      state: AssistPreviewSliceState,
      action: PayloadAction<TicketEvent>,
    ) {
      state.ticketEvents.push(action.payload);
    },

    // Streaming actions
    appendStreamChunk(
      state: AssistPreviewSliceState,
      action: PayloadAction<StreamChunk>,
    ) {
      state.streamChunks.push(action.payload);

      // Handle different chunk types
      if (action.payload.type === 'text' && action.payload.content) {
        state.agentAssistView.streamingContent += action.payload.content;
      } else if (
        action.payload.type === 'suggestion' &&
        action.payload.content
      ) {
        state.agentAssistView.suggestions.push(action.payload.content);
      } else if (action.payload.type === 'done') {
        state.agentAssistView.isStreaming = false;
        // Move streaming content to messages
        if (state.agentAssistView.streamingContent) {
          state.agentAssistView.messages.push({
            content: state.agentAssistView.streamingContent,
            message_id: uuidv4(),
            role: 'assistant',
          });
          state.agentAssistView.streamingContent = '';
        }
      }
    },

    clearError(state: AssistPreviewSliceState) {
      state.error = null;
    },

    clearStreamChunks(state: AssistPreviewSliceState) {
      state.streamChunks = [];
      state.agentAssistView.streamingContent = '';
    },

    // Initialize a new session
    initializeSession(
      state: AssistPreviewSliceState,
      action: PayloadAction<string>,
    ) {
      return {
        ...initialAssistPreviewState,
        config: state.config, // Preserve configuration
        session: {
          conversationId: null,
          previewSessionId: action.payload,
        },
      };
    },

    // Reset entire state
    resetAssistPreview() {
      return initialAssistPreviewState;
    },

    // Reset to clean state but preserve configuration
    resetSession(state: AssistPreviewSliceState) {
      return {
        ...initialAssistPreviewState,
        config: state.config,
        resetCounter: state.resetCounter + 1,
      };
    },

    // Configuration actions
    setContextVariables(
      state: AssistPreviewSliceState,
      action: PayloadAction<Record<string, boolean | number | string>>,
    ) {
      state.config.contextVariables = action.payload;
    },

    setConverseLoading(
      state: AssistPreviewSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.isConverseLoading = action.payload;
      if (action.payload) {
        state.agentAssistView.isStreaming = true;
      }
    },

    setError(
      state: AssistPreviewSliceState,
      action: PayloadAction<{ code?: string; message: string } | null>,
    ) {
      state.error = action.payload;
    },

    setIntentFilters(
      state: AssistPreviewSliceState,
      action: PayloadAction<Record<string, PreviewIntentFilter>>,
    ) {
      state.config.intentFilters = action.payload;
    },

    setLocale(state: AssistPreviewSliceState, action: PayloadAction<string>) {
      state.config.locale = action.payload;
    },

    // Response handling
    setPreviewResponse(
      state: AssistPreviewSliceState,
      action: PayloadAction<AssistPreviewResponse>,
    ) {
      const response = action.payload;

      state.session.conversationId = response.conversation_id;
      state.session.previewSessionId = response.preview_session_id;

      // Update Agent-User View
      state.agentUserView.messages = response.agent_user_view.messages;

      // Update Agent-Assist View
      state.agentAssistView.messages = response.agent_assist_view.messages;
      state.agentAssistView.suggestions =
        response.agent_assist_view.suggestions;
      state.agentAssistView.relatedDocuments =
        response.agent_assist_view.related_documents;
      state.agentAssistView.summary = response.agent_assist_view.summary;

      // Extract non-null widget components from messages
      state.agentAssistView.widgetComponents =
        response.agent_assist_view.messages
          .map(msg => msg.widget_component)
          .filter((wc): wc is WidgetComponent => wc != null);

      const nextIntentId =
        response.meta_view.current_intent ??
        response.meta_view.current_intent_id ??
        state.metaView.current_intent_id ??
        null;
      state.metaView = {
        ...response.meta_view,
        current_intent_id: nextIntentId,
        current_intent_title:
          response.meta_view.current_intent_title ??
          state.metaView.current_intent_title ??
          null,
      };

      // Clear reply loading only when suggestions have actually arrived
      // (setReplyLoading(true) clears suggestions, so any present are new)
      if (response.agent_assist_view.suggestions.length > 0) {
        state.isReplyLoading = false;
      }
      // isStarting and isConverseLoading are cleared by the hook after
      // the stream completes, so that loading indicators persist until
      // the full response is available.
    },

    setReplyLoading(
      state: AssistPreviewSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.isReplyLoading = action.payload;
      if (action.payload) {
        state.agentAssistView.suggestions = [];
      }
    },

    setStarting(
      state: AssistPreviewSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.isStarting = action.payload;
      if (action.payload) {
        state.agentAssistView.suggestions = [];
      }
    },

    // Pusher event handlers
    updateContextVariablesFromPusher(
      state: AssistPreviewSliceState,
      action: PayloadAction<Record<string, boolean | number | string>>,
    ) {
      state.metaView.context_variables = action.payload;
    },

    updateCurrentIntent(
      state: AssistPreviewSliceState,
      action: PayloadAction<{
        confidence: number;
        intent_id: string;
        intent_name: string;
      }>,
    ) {
      state.metaView.current_intent_id = action.payload.intent_id || null;
      state.metaView.current_intent_title = action.payload.intent_name || null;
    },

    updateRelatedDocuments(
      state: AssistPreviewSliceState,
      action: PayloadAction<RelatedArticle[]>,
    ) {
      state.agentAssistView.relatedDocuments = action.payload;
    },

    updateSuggestions(
      state: AssistPreviewSliceState,
      action: PayloadAction<string[]>,
    ) {
      state.agentAssistView.suggestions = action.payload;
    },

    updateSummary(
      state: AssistPreviewSliceState,
      action: PayloadAction<string>,
    ) {
      state.agentAssistView.summary = action.payload;
    },
  },
});

// Selectors
export const selectAssistPreviewSession = (
  state: RootState,
): AssistPreviewSliceState['session'] => state.assistPreview.session;

export const selectAssistPreviewConfig = (
  state: RootState,
): AssistPreviewSliceState['config'] => state.assistPreview.config;

export const selectAgentUserViewMessages = (
  state: RootState,
): AssistPreviewSliceState['agentUserView']['messages'] =>
  state.assistPreview.agentUserView.messages;

export const selectAgentAssistView = (
  state: RootState,
): AssistPreviewSliceState['agentAssistView'] =>
  state.assistPreview.agentAssistView;

export const selectAssistPreviewMetaView = (
  state: RootState,
): AssistPreviewSliceState['metaView'] => state.assistPreview.metaView;

export const selectAssistPreviewError = (
  state: RootState,
): AssistPreviewSliceState['error'] => state.assistPreview.error;

export const selectIsAssistPreviewLoading = (state: RootState): boolean =>
  state.assistPreview.isStarting ||
  state.assistPreview.isReplyLoading ||
  state.assistPreview.isConverseLoading;

export const selectIsAssistPreviewStarting = (state: RootState): boolean =>
  state.assistPreview.isStarting;

export const selectIsAssistPreviewReplyLoading = (state: RootState): boolean =>
  state.assistPreview.isReplyLoading;

export const selectIsAssistPreviewConverseLoading = (
  state: RootState,
): boolean => state.assistPreview.isConverseLoading;

export const selectIsAssistPreviewStreaming = (state: RootState): boolean =>
  state.assistPreview.agentAssistView.isStreaming;

export const selectStreamingContent = (state: RootState): string =>
  state.assistPreview.agentAssistView.streamingContent;

export const selectAssistPreviewSummary = (state: RootState): string =>
  state.assistPreview.agentAssistView.summary;

export const selectAssistPreviewSuggestions = (state: RootState): string[] =>
  state.assistPreview.agentAssistView.suggestions;

export const selectAssistPreviewRelatedDocuments = (
  state: RootState,
): RelatedArticle[] => state.assistPreview.agentAssistView.relatedDocuments;

export const selectAssistPreviewCurrentIntent = (
  state: RootState,
): string | null => {
  const { current_intent_id, current_intent_title } =
    state.assistPreview.metaView;
  if (current_intent_title) {
    return current_intent_title;
  }
  if (!current_intent_id) {
    return null;
  }
  const filter = state.workflowPreview.intentFilters.find(
    f => f.intentId === current_intent_id,
  );
  return filter?.intentName ?? current_intent_id;
};

export const selectAssistPreviewLanguage = (state: RootState): string =>
  state.assistPreview.metaView.language;

export const selectAssistPreviewLogs = (state: RootState): PreviewLog[] =>
  state.assistPreview.previewLogs;

export const selectAssistPreviewResetCounter = (state: RootState): number =>
  state.assistPreview.resetCounter;

export const selectAssistPreviewWidgetComponents = (
  state: RootState,
): WidgetComponent[] => state.assistPreview.agentAssistView.widgetComponents;

export const selectTicketEvents = (state: RootState): TicketEvent[] =>
  state.assistPreview.ticketEvents;

export default assistPreviewSlice.reducer;

export const {
  addPreviewLog,
  addTicketEvent,
  appendStreamChunk,
  clearError,
  clearStreamChunks,
  initializeSession,
  resetAssistPreview,
  resetSession,
  setContextVariables,
  setConverseLoading,
  setError,
  setIntentFilters,
  setLocale,
  setPreviewResponse,
  setReplyLoading,
  setStarting,
  updateContextVariablesFromPusher,
  updateCurrentIntent,
  updateRelatedDocuments,
  updateSuggestions,
  updateSummary,
} = assistPreviewSlice.actions;
